import type { IsoDate } from '../dates';
import { bestPrice, productOf } from '../price-book';
import type { Recipe } from '../recipe';
import { coverNeed, type ShoppingContext } from '../shopping';
import type { Stock } from '../stock';

/**
 * DE: Verbrauchskosten eines Rezepts in Cent (anteilige Packungspreise, ohne Grundzutaten).
 * EN: Consumption cost of a recipe in cents (prorated package prices, without staples).
 */
export function consumptionCents(
  recipe: Recipe,
  servings: number,
  context: ShoppingContext,
  date: IsoDate,
): number {
  const factor = servings / recipe.baseServings;
  return recipe.ingredients
    .filter((ingredient) => !context.staples.includes(ingredient.productId))
    .reduce((sum, ingredient) => {
      const product = productOf(context.book.catalog, ingredient.productId);
      const price = bestPrice(context.book, product.id, context.household.storeIds, date);
      return sum + (ingredient.quantity * factor * price.priceCents) / product.packSize;
    }, 0);
}

/**
 * DE: Was ganze Packungen zusätzlich kosten, wenn der Vorrat zuerst genutzt wird.
 * EN: What whole packages cost on top when the stock is used first.
 */
export function purchaseCents(
  needs: readonly { productId: string; quantity: number }[],
  dates: { need: IsoDate; trip: IsoDate },
  stock: Stock,
  context: ShoppingContext,
): { stock: Stock; cents: number } {
  let current = stock;
  let cents = 0;
  for (const { productId, quantity } of needs) {
    if (context.staples.includes(productId)) continue;
    const result = coverNeed(
      { date: dates.need, productId, quantity },
      dates.trip,
      current,
      context,
    );
    current = result.stock;
    cents += result.purchase ? result.purchase.price.priceCents * result.purchase.packages : 0;
  }
  return { stock: current, cents };
}

/**
 * DE: Zutatenmengen eines Rezepts für die gewünschten Portionen.
 * EN: Ingredient quantities of a recipe for the wanted servings.
 */
export function scaledIngredients(recipe: Recipe, servings: number) {
  const factor = servings / recipe.baseServings;
  return recipe.ingredients.map((ingredient) => ({
    productId: ingredient.productId,
    quantity: ingredient.quantity * factor,
  }));
}
